import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { Form, Input, InputNumber, Select, DatePicker, message, Button as AntButton } from "antd";
import { PlusOutlined, MinusCircleOutlined, ArrowLeftOutlined, CheckCircleOutlined } from "@ant-design/icons";
import { fetchSellerListings, fetchDealerPaymentAccounts, createSale, fetchInvoiceSettings } from "../api/auth";
import { useAuth } from "../auth/AuthContext";
import { PageHeader, PageShell, Panel } from "../components/Page";
import type { CreateSaleInput } from "../api/auth";

interface FormValues {
  listingId: string;
  bikePrice: number;
  discountPercent: number;
  taxRate: number;
  customerName: string;
  customerPhone: string;
  paymentAccountId: string;
  firstPaymentAmount: number;
  transactionReference: string;
  saleDate: string | Date;
  notes: string;
  loanMonths?: number;
  interestRate?: number;
  charges?: {
    kind: string;
    label: string;
    amount: string | number;
    sortOrder?: number;
    ratePercent?: string | number;
    baseAmount?: string | number;
    type?: "POSITIVE" | "NEGATIVE";
    costAmount?: string | number;
  }[];
}

const CHARGE_KINDS = [
  "TAX",
  "REGISTRATION",
  "ACCESSORY",
  "SERVICE",
  "DELIVERY",
  "OTHER",
  "DISCOUNT",
  "WARRANTY",
] as const;

const DRAFT_KEY = "add-sale-draft";

export default function AddSalePage() {
  const { seller } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<FormValues>();
  const [selectedBike, setSelectedBike] = useState<{ id: string; title: string; price?: number; publicId: string } | null>(null);
  const [additionalCharges, setAdditionalCharges] = useState<{
    label: string;
    kind: string;
    type: "POSITIVE" | "NEGATIVE";
    amount: string;
    ratePercent?: string;
    baseAmount?: string;
    costAmount?: string;
  }[]>([]);
  const [showLoanCalc, setShowLoanCalc] = useState(false);
  const [loanCompanyInfo, setLoanCompanyInfo] = useState("");
  const [receiptUrl, setReceiptUrl] = useState<string>("");
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const draftRestoredRef = useRef(false);
  const pendingBikeIdRef = useRef<string | null>(null);

  const { data: activeData } = useQuery({
    queryKey: ["active-listings-dropdown"],
    queryFn: () => fetchSellerListings(1, 200),
  });

  const { data: paymentAccounts = [] } = useQuery({
    queryKey: ["dealer-payment-accounts"],
    queryFn: () => fetchDealerPaymentAccounts(),
    enabled: !!seller,
  });

  const { data: invoiceSettings } = useQuery({
    queryKey: ["invoice-settings"],
    queryFn: () => fetchInvoiceSettings(),
    staleTime: 5 * 60 * 1000,
  });

  const availableBikes = useMemo(
    () => (activeData?.rows ?? []).filter((r) => r.status === "PUBLISHED" || r.status === "PAUSED"),
    [activeData],
  );

  const bikeOptions = useMemo(() => availableBikes.map((b) => ({
    value: b.id,
    label: `${b.title} (${b.publicId}) ${b.price ? `— ৳ ${Number(b.price).toLocaleString()}` : ""}`,
    price: b.price,
  })), [availableBikes]);

  const saveDraft = useCallback(() => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      try {
        const values = form.getFieldsValue();
        const draft = {
          formValues: values,
          additionalCharges,
          showLoanCalc,
          loanCompanyInfo,
          receiptUrl,
          selectedBikeId: values.listingId || null,
          savedAt: Date.now(),
        };
        localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
      } catch {
        // ignore write errors
      }
    }, 500);
  }, [form, additionalCharges, showLoanCalc, loanCompanyInfo, receiptUrl]);

  const allValues = Form.useWatch([], form);
  useEffect(() => { saveDraft(); }, [allValues, saveDraft]);
  useEffect(() => { saveDraft(); }, [additionalCharges, showLoanCalc, loanCompanyInfo, receiptUrl, saveDraft]);

  useEffect(() => {
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, []);

  useEffect(() => {
    if (draftRestoredRef.current) return;
    draftRestoredRef.current = true;
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (!raw) return;
      const draft = JSON.parse(raw);
      if (draft.formValues) form.setFieldsValue(draft.formValues);
      // eslint-disable-next-line react-hooks/set-state-in-effect
      if (draft.additionalCharges) setAdditionalCharges(draft.additionalCharges);
      if (draft.showLoanCalc) setShowLoanCalc(draft.showLoanCalc);
      if (draft.loanCompanyInfo) setLoanCompanyInfo(draft.loanCompanyInfo);
      if (draft.receiptUrl) setReceiptUrl(draft.receiptUrl);
      if (draft.selectedBikeId) pendingBikeIdRef.current = draft.selectedBikeId;
      message.info("Draft restored from previous session");
    } catch {
      // ignore corrupted draft
    }
  }, [form]);

  useEffect(() => {
    if (!pendingBikeIdRef.current || !availableBikes.length) return;
    const bike = availableBikes.find((b) => String(b.id) === String(pendingBikeIdRef.current));
    if (bike) {
      setSelectedBike(bike);
      pendingBikeIdRef.current = null;
    }
  }, [availableBikes]);

  const handleBikeChange = useCallback((bikeId: string) => {
    const bike = availableBikes.find((b) => String(b.id) === String(bikeId));
    if (bike) {
      setSelectedBike(bike);
      setAdditionalCharges([]);
      setLoanCompanyInfo("");
      form.setFieldsValue({
        listingId: bike.id,
        bikePrice: bike.price || 0,
        discountPercent: invoiceSettings?.salesDefaults?.defaultDiscountPercent ?? 0,
        charges: invoiceSettings?.salesDefaults?.defaultCharges ?? [],
        paymentAccountId: invoiceSettings?.salesDefaults?.defaultPaymentAccountId ?? "",
        notes: invoiceSettings?.salesDefaults?.defaultNotes ?? "",
        saleDate: new Date().toISOString().slice(0, 10),
      });
    }
  }, [availableBikes, form, invoiceSettings]);

  const removeCharge = useCallback((idx: number) => {
    setAdditionalCharges((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  const updateCharge = useCallback((idx: number, field: keyof typeof additionalCharges[0], value: string) => {
    setAdditionalCharges((prev) =>
      prev.map((c, i) =>
        i === idx ? { ...c, [field]: field === "type" ? (value as "POSITIVE" | "NEGATIVE") : value } : c,
      ),
    );
  }, []);

  const addChargeRow = useCallback((type: "POSITIVE" | "NEGATIVE") => {
    setAdditionalCharges((prev) => [...prev, { label: "", kind: "OTHER", type, amount: "", ratePercent: "", baseAmount: "", costAmount: "" }]);
  }, []);

  const sellingPriceWatch = Form.useWatch("bikePrice", form);
  const discountPercentWatch = Form.useWatch("discountPercent", form);
  const taxRateWatch = Form.useWatch("taxRate", form);

  const sp = Number(sellingPriceWatch) || 0;
  const dp = Number(discountPercentWatch) || 0;
  const tx = Number(taxRateWatch) || 0;

  const taxAmount = (sp * tx) / 100;
  const discountedPrice = sp - sp * (dp / 100);

  const positiveCharges = additionalCharges.filter((c) => c.type === "POSITIVE");
  const negativeCharges = additionalCharges.filter((c) => c.type === "NEGATIVE");

  const positiveChargesSum = positiveCharges.reduce((s, c) => s + (parseFloat(c.amount) || 0), 0);
  const negativeChargesSum = negativeCharges.reduce((s, c) => s + (parseFloat(c.amount) || 0), 0);
  const totalChargesSum = positiveChargesSum - negativeChargesSum;

  const totalItemSale = discountedPrice + taxAmount + totalChargesSum;

  const dpAmount = Number(Form.useWatch("firstPaymentAmount", form)) || 0;

  const paymentAccountWatch = Form.useWatch("paymentAccountId", form);
  const selectedPayAccount = paymentAccounts.find((a) => a.id === paymentAccountWatch);
  const isBankPayment = selectedPayAccount && selectedPayAccount.accountType !== "CASH";
  const showDpReceipt = dpAmount > 0 && isBankPayment;

  const cashOnDelivery = Math.max(0, totalItemSale - dpAmount);

  const monthlyRate = Number(Form.useWatch("interestRate", form)) / 100 / 12;
  const loanMonthsVal = Number(Form.useWatch("loanMonths", form)) || 0;

  const emi =
    loanMonthsVal > 0 && monthlyRate > 0 && cashOnDelivery > 0
      ? (cashOnDelivery * monthlyRate * Math.pow(1 + monthlyRate, loanMonthsVal)) /
        (Math.pow(1 + monthlyRate, loanMonthsVal) - 1)
      : 0;

  const createMutation = useMutation({
    mutationFn: (data: CreateSaleInput) => createSale(data),
    onSuccess: (result: { id: string }) => {
      localStorage.removeItem(DRAFT_KEY);
      queryClient.invalidateQueries({ queryKey: ["sales-list"] });
      queryClient.invalidateQueries({ queryKey: ["sales-stats"] });
      queryClient.invalidateQueries({ queryKey: ["active-listings-dropdown"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["dealer-payment-accounts"] });
      queryClient.invalidateQueries({ queryKey: ["dealer-ledger"] });
      queryClient.invalidateQueries({ queryKey: ["dealer-ledger-stats"] });
      message.success("Sale recorded and invoice generated.");
      navigate(`/sales/${result.id}`);
    },
    onError: (err: Error) => message.error(err.message),
  });

  const handleSubmit = async (values: FormValues) => {
    if (showDpReceipt && !values.transactionReference?.trim()) {
      message.error("Transaction ID / Receipt Number is required for non-cash down payments");
      return;
    }
    if (!values.listingId) {
      message.error("Please select a bike");
      return;
    }
    if (!values.bikePrice) {
      message.error("Sale price is required");
      return;
    }

    const payload: CreateSaleInput = {
      listingId: values.listingId,
      bikePrice: sp,
      discountPercent: dp,
      discountAmount: sp * (dp / 100),
      customerName: values.customerName?.trim(),
      customerPhone: values.customerPhone?.trim(),
      paymentAccountId: values.paymentAccountId || undefined,
      firstPaymentAmount: dpAmount,
      transactionReference: values.transactionReference?.trim() || undefined,
      saleDate: values.saleDate ? new Date(values.saleDate).toISOString() : undefined,
      notes: values.notes?.trim() || undefined,
    };

    const chargesToSend = additionalCharges
      .filter((c) => c.label && c.amount)
      .map((c, i) => ({
        kind: c.kind,
        label: c.label,
        amount: parseFloat(c.amount),
        type: c.type,
        ratePercent: c.ratePercent ? parseFloat(c.ratePercent) : undefined,
        baseAmount: c.baseAmount ? parseFloat(c.baseAmount) : undefined,
        costAmount: c.costAmount ? parseFloat(c.costAmount) : undefined,
        sortOrder: i,
      }));

    if (chargesToSend.length > 0) {
      payload.charges = chargesToSend;
    }

    createMutation.mutate(payload);
  };

  const isBusy = createMutation.isPending;

  const formatTk = (val: number) => `৳ ${val.toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

  const renderPositiveCharges = () => positiveCharges.map((charge) => {
    const realIdx = additionalCharges.indexOf(charge);
    return (
      <div
        key={realIdx}
        className="flex items-start gap-2 bg-white rounded-lg p-3 border border-gray-200"
      >
        <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <Form.Item name={["charges", realIdx, "kind"]} noStyle>
            <Select
              placeholder="Kind"
              className="w-full"
              value={charge.kind}
              onChange={(v) => updateCharge(realIdx, "kind", v)}
            >
              {CHARGE_KINDS.map((k) => (
                <Select.Option key={k} value={k}>{k}</Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name={["charges", realIdx, "label"]} noStyle>
            <Input
              placeholder="e.g. Registration Fee"
              value={charge.label}
              onChange={(e) => updateCharge(realIdx, "label", e.target?.value ?? "")}
            />
          </Form.Item>
<Form.Item name={["charges", realIdx, "amount"]} noStyle>
            <InputNumber
              min={0}
              placeholder="Amount"
              value={charge.amount ? parseFloat(charge.amount) : undefined}
              onChange={(val: number | null) => updateCharge(realIdx, "amount", String(val ?? ""))}
className="w-full text-right"
               prefix="৳"
             />
          </Form.Item>
          <Form.Item name={["charges", realIdx, "costAmount"]} noStyle>
            <InputNumber
              min={0}
              placeholder="Cost (optional)"
              value={charge.costAmount ? parseFloat(charge.costAmount) : undefined}
              onChange={(val: number | null) => updateCharge(realIdx, "costAmount", String(val ?? ""))}
className="w-full text-right"
               prefix="৳"
             />
          </Form.Item>
        </div>
        <AntButton
          danger
          size="small"
          onClick={() => removeCharge(realIdx)}
        >
          <MinusCircleOutlined />
        </AntButton>
      </div>
    );
  });

  const renderNegativeCharges = () => negativeCharges.map((charge) => {
    const realIdx = additionalCharges.indexOf(charge);
    return (
      <div
        key={realIdx}
        className="flex items-start gap-2 bg-white rounded-lg p-3 border border-gray-200"
      >
        <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <Form.Item name={["charges", realIdx, "kind"]} noStyle>
            <Select
              placeholder="Kind"
              className="w-full"
              value={charge.kind}
              onChange={(v) => updateCharge(realIdx, "kind", v)}
            >
              {CHARGE_KINDS.map((k) => (
                <Select.Option key={k} value={k}>{k}</Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name={["charges", realIdx, "label"]} noStyle>
            <Input
              placeholder="e.g. Trade-in Allowance"
              value={charge.label}
              onChange={(e) => updateCharge(realIdx, "label", e.target?.value ?? "")}
            />
          </Form.Item>
<Form.Item name={["charges", realIdx, "amount"]} noStyle>
            <InputNumber
              min={0}
              placeholder="Amount"
              value={charge.amount ? parseFloat(charge.amount) : undefined}
              onChange={(val: number | null) => updateCharge(realIdx, "amount", String(val ?? ""))}
className="w-full text-right"
               prefix="৳"
             />
          </Form.Item>
          <Form.Item name={["charges", realIdx, "costAmount"]} noStyle>
            <InputNumber
              min={0}
              placeholder="Cost (optional)"
              value={charge.costAmount ? parseFloat(charge.costAmount) : undefined}
              onChange={(val: number | null) => updateCharge(realIdx, "costAmount", String(val ?? ""))}
className="w-full text-right"
               prefix="৳"
             />
          </Form.Item>
        </div>
        <AntButton
          danger
          size="small"
          onClick={() => removeCharge(realIdx)}
        >
          <MinusCircleOutlined />
        </AntButton>
      </div>
    );
  });

  return (
    <PageShell>
      <PageHeader
        eyebrow="Transactions"
        title="Record New Sale"
        description="Enter the bike, price, charges, buyer and payment details in one step."
        actions={
          <AntButton onClick={() => navigate("/sales/manage")} icon={<ArrowLeftOutlined />}>
            Back to Manage Sale
          </AntButton>
        }
      />

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Left Side - Inputs */}
        <div className="lg:w-1/2 space-y-6">
          {/* Bike Selection */}
          <Panel title="Select Bike" description="Choose a published or paused listing from your inventory." bodyClassName="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Form.Item
                name="listingId"
                label="Select Bike"
                rules={[{ required: true, message: "Please select a bike to mark as sold" }]}
              >
                <Select
                  placeholder="Search from published/paused inventory…"
                  showSearch
                  optionFilterProp="children"
                  onChange={handleBikeChange}
                  filterOption={(input: string, option?: { children?: unknown }) =>
                    (option?.children as unknown as string ?? "").toLowerCase().includes(input.toLowerCase())
                  }
                  className="w-full"
                  loading={!activeData}
                >
                  {bikeOptions.map((b) => (
                    <Select.Option key={b.value} value={b.value} label={b.label}>
                      {b.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                name="bikePrice"
                label="Final Sale Price (BDT)"
                rules={[{ required: true, message: "Sale price is required" }]}
              >
                <InputNumber className="w-full" min={0} prefix="৳" placeholder="Agreed price in BDT" />
              </Form.Item>
            </div>

            {selectedBike && (
              <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex flex-wrap justify-between items-center">
                  <span className="font-semibold text-blue-800">Selected Bike:</span>
                  <span className="font-bold text-blue-900 tnum">{selectedBike.title} ({selectedBike.publicId})</span>
                </div>
                {selectedBike.price && (
                  <p className="mt-1 text-sm text-blue-700">
                    Listing price: <span className="tnum">৳ {Number(selectedBike.price).toLocaleString()}</span>
                  </p>
                )}
              </div>
            )}
          </Panel>

          {/* Sales Price Calculator */}
          <Panel title="Sales Price Calculator" bodyClassName="p-6">
            <div className="space-y-4">
              <div className="flex flex-wrap justify-between items-center">
                <h3 className="text-lg font-semibold">Sales Price Calculator</h3>
                <AntButton
                  type="default"
                  size="small"
                  onClick={() => {
                    form.setFieldsValue({
                      listingId: selectedBike?.id,
                      bikePrice: selectedBike?.price || 0,
                      discountPercent: invoiceSettings?.salesDefaults?.defaultDiscountPercent ?? 0,
                      taxRate: 0,
                      firstPaymentAmount: 0,
                      loanMonths: 0,
                      interestRate: 0,
                    });
                    setAdditionalCharges(invoiceSettings?.salesDefaults?.defaultCharges?.map((c) => ({
                      label: c.label,
                      kind: c.kind,
                      type: c.type ?? "POSITIVE",
                      amount: String(c.amount),
                      ratePercent: c.ratePercent ? String(c.ratePercent) : "",
                      baseAmount: c.baseAmount ? String(c.baseAmount) : "",
                      costAmount: c.costAmount ? String(c.costAmount) : "",
                    })) ?? []);
                    setLoanCompanyInfo("");
                  }}
                >
                  Reset Calculator
                </AntButton>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <div className="flex items-center gap-2">
                  <label className="font-semibold w-1/2 text-gray-700">Selling Price:</label>
                  <Form.Item name="bikePrice" className="w-1/2 mb-0" rules={[{ required: true }]}>
                    <InputNumber min={0} className="w-full text-right font-bold" prefix="৳" />
                  </Form.Item>
                </div>

                <div className="flex items-center gap-2">
                  <Form.Item name="discountPercent" label="Discount %" className="w-1/2 mb-0">
                    <InputNumber min={0} max={100} step={0.01} className="w-full" suffix="%" />
                  </Form.Item>
                  <Form.Item label="Discount Amount" className="w-1/2 mb-0">
                    <InputNumber
                      value={sp * (dp / 100)}
                      readOnly
                      className="w-full text-right bg-gray-100 text-gray-600 font-bold"
                      formatter={(val) => formatTk(Number(val))}
                    />
                  </Form.Item>
                </div>

                <div className="flex items-center gap-2">
                  <Form.Item name="taxRate" label="Sales Tax (%)" className="w-1/2 mb-0">
                    <InputNumber min={0} step={0.1} className="w-full" suffix="%" />
                  </Form.Item>
                  <Form.Item label="Tax Amount" className="w-1/2 mb-0">
                    <InputNumber
                      value={taxAmount}
                      readOnly
                      className="w-full text-right bg-gray-100 text-gray-600 font-bold"
                      formatter={(val) => formatTk(Number(val))}
                    />
                  </Form.Item>
                </div>

                <div className="flex items-center gap-2">
                  <label className="font-semibold w-1/2 text-gray-700">Discounted Price:</label>
                  <Form.Item className="w-1/2 mb-0">
                    <InputNumber
                      value={discountedPrice}
                      readOnly
                      className="w-full text-right bg-gray-100 text-gray-600 font-bold"
                      formatter={(val) => formatTk(Number(val))}
                    />
                  </Form.Item>
                </div>
              </div>

              {/* Other Charges (Manual) - positive */}
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <div className="flex flex-wrap items-center justify-between mb-3">
                  <h4 className="font-semibold">Other Charges (Add-ons)</h4>
                  <AntButton
                    danger
                    size="small"
                    icon={<PlusOutlined />}
                    onClick={() => addChargeRow("POSITIVE")}
                  >
                    Add Charge
                  </AntButton>
                </div>
                {positiveCharges.length === 0 && (
                  <p className="text-xs text-gray-400 italic mb-3">No additional charges</p>
                )}
                <div className="space-y-3">
                  {renderPositiveCharges()}
                </div>
              </div>

              {/* Deductions - negative */}
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <div className="flex flex-wrap items-center justify-between mb-3">
                  <h4 className="font-semibold">Deductions</h4>
                  <AntButton
                    danger
                    size="small"
                    icon={<PlusOutlined />}
                    onClick={() => addChargeRow("NEGATIVE")}
                  >
                    Add Deduction
                  </AntButton>
                </div>
                {negativeCharges.length === 0 && (
                  <p className="text-xs text-gray-400 italic mb-3">No deductions</p>
                )}
                <div className="space-y-3">
                  {renderNegativeCharges()}
                </div>
              </div>

              {/* Total Item Sale */}
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-lg text-blue-800">Total Item Sale</span>
                  <span className="font-bold text-xl text-blue-900 tnum">
                    {formatTk(totalItemSale)}
                  </span>
                </div>
              </div>

              {/* Down Payment */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <label className="font-semibold w-1/2 text-gray-700">Down Payment:</label>
                  <Form.Item name="firstPaymentAmount" className="w-1/2 mb-0">
                    <InputNumber min={0} className="w-full text-right font-bold" prefix="৳" />
                  </Form.Item>
                </div>

                {/* Payment Account */}
                <Form.Item
                  name="paymentAccountId"
                  label="Payment Account"
                  rules={[{ required: true, message: "Please select a payment account" }]}
                >
                  <Select
                    placeholder="Select receiving account"
                    className="w-full"
                    loading={!paymentAccounts.length}
                    showSearch
                    allowClear
                    notFoundContent={
                      paymentAccounts.length === 0 ? (
                        <div className="py-2 text-center text-sm text-gray-500">No payment accounts configured</div>
                      ) : undefined
                    }
                    filterOption={(input, option) =>
                      (option?.label as string)?.toLowerCase().includes(input?.toLowerCase() ?? "")
                    }
                  >
                    {paymentAccounts.map((acc) => (
                      <Select.Option key={acc.id} value={acc.id}>
                        {acc.name} — ৳{Number(acc.currentBalance).toLocaleString()} ({acc.accountType})
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>

                {/* Down Payment Reference & Receipt */}
                {showDpReceipt && (
                  <div className="border-t border-gray-200 pt-3 mt-1 space-y-3">
                    <Form.Item
                      name="transactionReference"
                      label="Down Payment Reference / Transaction ID"
                      rules={[{ required: true, message: "Transaction ID is required for non-cash payments" }]}
                    >
                      <Input placeholder="Required — e.g. TrxID, cheque no, receipt no" />
                    </Form.Item>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                      <p className="text-sm text-gray-600">Receipt Upload (optional)</p>
                      <Input type="file" accept="image/*,application/pdf" onChange={(e) => {
                        const file = e.target?.files?.[0];
                        if (file) setReceiptUrl(URL.createObjectURL(file));
                      }} />
                    </div>
                  </div>
                )}

                {/* Cash on Delivery / Financed Amount */}
                <div className="flex items-center gap-2">
                  <label className="font-bold w-1/2 text-gray-700">
                    {showLoanCalc ? "Loan Amount (Financed):" : "Cash on Delivery:"}
                  </label>
                  <Form.Item className="w-1/2 mb-0">
                    <InputNumber
                      value={cashOnDelivery}
                      readOnly
                      className="w-full text-right font-bold bg-gray-50"
                      formatter={(val) => formatTk(Number(val))}
                    />
                  </Form.Item>
                </div>

                {/* Loan Toggle */}
                <div className="mt-2">
                  <AntButton
                    htmlType="button"
                    type={showLoanCalc ? "default" : "primary"}
                    onClick={() => {
                      if (!showLoanCalc) {
                        form.setFieldsValue({ loanMonths: 36, interestRate: 10 });
                        setShowLoanCalc(true);
                      } else {
                        form.setFieldsValue({ loanMonths: 0, interestRate: 0 });
                        setLoanCompanyInfo("");
                        setShowLoanCalc(false);
                      }
                    }}
                  >
                    {showLoanCalc ? "Remove Loan Info" : "Add Loan Info"}
                  </AntButton>
                </div>

                {/* Loan Inputs */}
                {showLoanCalc && (
                  <div className="space-y-3 mt-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <Form.Item name="loanMonths" label="Loan Terms (Months)">
                        <InputNumber min={1} className="w-full" />
                      </Form.Item>
                      <Form.Item name="interestRate" label="Interest Rate (%)">
                        <InputNumber min={0} step={0.1} className="w-full" />
                      </Form.Item>
                    </div>
                    <div>
                      <Form.Item label="Loan Company Info">
                        <Input.TextArea
                          rows={3}
                          maxLength={255}
                          placeholder="Loan company info..."
                          value={loanCompanyInfo}
                          onChange={(e) => setLoanCompanyInfo(e.target?.value ?? "")}
                        />
                      </Form.Item>
                    </div>
                    {emi > 0 && (
                      <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                        <p className="font-medium text-blue-800">Estimated EMI: <span className="tnum">৳ {emi.toLocaleString(undefined, { minimumFractionDigits: 2 })} / month</span></p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </Panel>

          {/* Customer Details */}
          <Panel title="Buyer Details" description="Enter the buyer's information. This will appear on the invoice." bodyClassName="p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Form.Item
                name="customerName"
                label="Buyer Full Name"
                rules={[{ required: true, message: "Buyer name is required" }]}
              >
                <Input placeholder="e.g. Tanvir Rahman" />
              </Form.Item>
              <Form.Item
                name="customerPhone"
                label="Buyer Contact Phone"
                rules={[{ required: true, message: "Buyer phone is required" }]}
              >
                <Input placeholder="017xxxxxxxx" />
              </Form.Item>
            </div>
            <Form.Item name="notes" label="Notes / Remarks">
              <Input placeholder="Invoice reference or receipt notes" />
            </Form.Item>
          </Panel>

          {/* Transaction Date */}
          <Panel title="Transaction Date" bodyClassName="p-6">
            <Form.Item name="saleDate" label="Sale Date" rules={[{ required: true, message: "Sale date is required" }]}>
              <DatePicker className="w-full" format="YYYY-MM-DD" />
            </Form.Item>
          </Panel>
        </div>

        {/* Right Side - Summary */}
        <div className="lg:w-1/2 space-y-5">
          <Panel title="Vehicle Sale Summary" className="sticky top-24" bodyClassName="p-6">
            <div className="bg-primary text-white p-4 rounded text-center text-xl font-bold mb-4">
              Sale Summary
            </div>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between items-center">
                <span className="font-medium text-ink-muted text-sm">Selling Price</span>
                <span className="tnum font-bold text-lg">{formatTk(sp)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="font-medium text-ink-muted text-sm">Discount</span>
                <span className="tnum text-red-600 font-bold text-lg">-{formatTk(sp * dp / 100)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="font-medium text-ink-muted text-sm">Discounted Price</span>
                <span className="tnum font-bold text-xl">{formatTk(discountedPrice)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="font-medium text-ink-muted text-sm">Sales Tax</span>
                <span className="tnum font-bold text-lg">{formatTk(taxAmount)}</span>
              </div>
              {positiveChargesSum > 0 && (
                <div className="flex justify-between items-center">
                  <span className="font-medium text-ink-muted text-sm">Other Charges</span>
                  <span className="tnum text-emerald-600 font-bold text-lg">+{formatTk(positiveChargesSum)}</span>
                </div>
              )}
              {negativeChargesSum > 0 && (
                <div className="flex justify-between items-center">
                  <span className="font-medium text-ink-muted text-sm">Deductions</span>
                  <span className="tnum text-red-600 font-bold text-lg">-{formatTk(negativeChargesSum)}</span>
                </div>
              )}
              <div className="border-t border-gray-200 pt-2" />
              <div className="flex justify-between items-center font-bold text-lg">
                <span>Total Item Sale</span>
                <span className="tnum">{formatTk(totalItemSale)}</span>
              </div>
              <div className="border-t border-gray-200 pt-2" />
              <div className="flex justify-between items-center">
                <span className="font-medium text-ink-muted text-sm">Down Payment</span>
                <span className="tnum font-bold text-lg">{formatTk(dpAmount)}</span>
              </div>
              <div className="flex justify-between items-center font-bold text-lg">
                <span>{showLoanCalc ? "Loan Amount" : "Cash on Delivery"}</span>
                <span className="tnum font-bold text-lg">{formatTk(cashOnDelivery)}</span>
              </div>
              {showLoanCalc && emi > 0 && (
                <div className="flex justify-between items-center">
                  <span className="font-medium text-ink-muted text-sm">Estimated EMI</span>
                  <span className="tnum">{formatTk(emi)} / month</span>
                </div>
              )}
            </div>

            <div className="mt-6 pt-4 border-t border-gray-200 space-y-3">
              <AntButton type="primary" htmlType="submit" loading={isBusy} icon={<CheckCircleOutlined />} className="w-full">
                Confirm & Save Sale
              </AntButton>
              <AntButton variant="text" className="w-full" onClick={() => navigate("/sales/manage")}>
                Cancel
              </AntButton>
            </div>
          </Panel>
        </div>
      </div>

      <Form form={form} layout="vertical" onFinish={handleSubmit} initialValues={{ saleDate: new Date().toISOString().slice(0, 10) }}>
        {/* Form is controlled by the submit buttons in the summary panel */}
      </Form>
    </PageShell>
  );
}